import { EmbeddedMessage, WhatsApp } from 'constants/messageMediums';
import {
  selectIsInAppRecallUIFeatureFlagOn,
  selectIsWebInAppReportingFeatureFlagOn,
} from 'core/userContext';
import { useNBAContext } from 'modules/ai/nba/context/NBAContext';
import { MessageMedium } from 'modules/graphql/types';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, withRouter } from 'react-router';
import { Skeleton } from 'ui/skeleton';

import { AudienceRetentionOverview } from './AudienceRetentionOverview';
import { EngagementOverview } from './EngagementOverview';
import { LayoutTemplate } from './LayoutTemplate';
import { MetricBreakdownCard } from './MetricBreakdownCard';
import { OverviewDeliveryProgressBarChart } from './OverviewDeliveryProgressBarChart';
import { PinnedMetrics } from './PinnedMetrics';
import { CampaignNBACard } from './nba/CampaignNBACard';

import { DELIVERY_GRAPH_NICENAME } from '../constants';
import { useWhatsAppGAFF } from '../hooks';
import {
  selectCampaignAnalyticsStatsForTimerange,
  selectIsLoading,
  selectMessageMedium,
  selectTopBounceReasonsBreakdownBuckets,
} from '../selectors';
import { parseMetricsForDeliveriesGraph } from '../utils';
import { RepliesOverview } from './RepliesOverview';
import {
  OverviewDeliveryChartsContainer,
  OverviewRowFullWidth,
  OverviewRowWithTwoEqualColumns,
  SkeletonWrapper,
} from './Styled';

export const CampaignAnalytics = () => {
  const isWhatsAppGAEnabled = useWhatsAppGAFF();
  const isLoading = useSelector(selectIsLoading);
  const { search } = useLocation();
  const inAppFF = useSelector(selectIsWebInAppReportingFeatureFlagOn);
  const inAppRecallFF = useSelector(selectIsInAppRecallUIFeatureFlagOn);
  const statsForTimeRange = useSelector(selectCampaignAnalyticsStatsForTimerange);
  const messageMedium: MessageMedium = useSelector(selectMessageMedium);
  const buckets = useSelector(selectTopBounceReasonsBreakdownBuckets);
  const { displayNBA } = useNBAContext();

  const [areOpenMetricsSelected, setAreOpenMetricsSelected] = useState(false);
  const deliveryGraphData = parseMetricsForDeliveriesGraph(
    DELIVERY_GRAPH_NICENAME(inAppRecallFF)[messageMedium] || [],
    statsForTimeRange?.results,
    buckets,
    inAppFF,
  );
  const nbaPrompt = displayNBA ? <CampaignNBACard messageMedium={messageMedium} /> : <></>;
  const isEmbedded = messageMedium === EmbeddedMessage;
  const isWhatsApp = messageMedium === WhatsApp;
  const isWhatsAppGA = isWhatsApp && isWhatsAppGAEnabled;
  const OverviewRowComponent = isEmbedded ? OverviewRowFullWidth : OverviewRowWithTwoEqualColumns;
  return (
    <LayoutTemplate
      contentHeader={nbaPrompt}
      pageName="Overview"
      canShowFPOBanner={areOpenMetricsSelected}
    >
      <PinnedMetrics setAreOpenMetricsSelected={setAreOpenMetricsSelected} />
      {messageMedium !== EmbeddedMessage && (
        <MetricBreakdownCard
          title={'Delivery'}
          linkout={{
            href: '/analytics/campaignPerformance/deliveries',
            search,
          }}
          data-test="delivery-overview"
        >
          <OverviewDeliveryChartsContainer>
            {isLoading
              ? [1, 2, 3].map((i) => (
                  <SkeletonWrapper key={i} height={128}>
                    <Skeleton />
                  </SkeletonWrapper>
                ))
              : deliveryGraphData.map((data: Record<string, any>, i: number) => (
                  <OverviewDeliveryProgressBarChart key={i} data={data} />
                ))}
          </OverviewDeliveryChartsContainer>
        </MetricBreakdownCard>
      )}
      <OverviewRowComponent>
        {(!isWhatsApp || isWhatsAppGA) && <EngagementOverview />}
        {!isEmbedded && <AudienceRetentionOverview />}
      </OverviewRowComponent>
      {isWhatsApp && isWhatsAppGA && (
        <OverviewRowWithTwoEqualColumns>
          <RepliesOverview />
          <div />
        </OverviewRowWithTwoEqualColumns>
      )}
    </LayoutTemplate>
  );
};

export default withRouter(CampaignAnalytics);
